import FormatCurrency from 'components/Common/FormatCurrency'
import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import ModalContext from 'contexts/ModalContext'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import * as OFFER_TYPES from 'constants/offer'
import { FormattedMessage, useIntl } from 'react-intl'

import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Group from 'components/utils/Group'

interface Props {
  commissionPercentage: number
  productType: App.ProductType
  offerTotal?: number
  multipleCommissions?: boolean
  greatestCommission: number
  minBaseCommission?: number
  maxBaseCommission?: number
  includesTaxesAndFees?: boolean
  includesMargin?: boolean
  incentivePercentage?: number
}

function PriceRowCommission(props: Props) {
  const {
    commissionPercentage,
    productType,
    offerTotal = 0,
    multipleCommissions = false,
    greatestCommission,
    minBaseCommission,
    maxBaseCommission,
    includesTaxesAndFees = true,
    includesMargin = true,
    incentivePercentage = 0,
  } = props

  const includesAllFees = includesTaxesAndFees && includesMargin

  const intl = useIntl()
  const hasTotal = offerTotal > 0

  const hotelLabel = intl.formatMessage({
    id: 'BOglDXqFdQpJjMQXwqKn',
    defaultMessage: 'hotel',
    description: 'Product type display name for hotel offers in commission details',
  })
  const tourLabel = intl.formatMessage({
    id: 'RWtku4g6HeUD9NeCWfvQ',
    defaultMessage: 'tour',
    description: 'Product type display name for tour offers in commission details',
  })
  const cruiseLabel = intl.formatMessage({
    id: 'vEMeqeHSJbMLvQsbVIzC',
    defaultMessage: 'cruise',
    description: 'Product type display name for cruise offers in commission details',
  })
  const carHireLabel = intl.formatMessage({
    id: 'H_vxhF9Wy1P1hiLsmjkY',
    defaultMessage: 'car hire',
    description: 'Product type display name for car hire offers in commission details',
  })

  const dealLabel = intl.formatMessage({
    id: 'oMrrHEno6ejQW1VQ2ULd',
    defaultMessage: 'deal',
    description: 'Product type display name for deal offers in commission details',
  })

  const villaLabel = intl.formatMessage({
    id: 'K_HYbSYQEsqT61Ggn5YY',
    defaultMessage: 'villa',
    description: 'Product type display name for villa offers in commission details',
  })

  const experienceLabel = intl.formatMessage({
    id: '9z-ZoILkKe3uOfA1WH6E',
    defaultMessage: 'experience',
    description: 'Product type display name for experience offers in commission details',
  })

  const flightLabel = intl.formatMessage({
    id: '2cVbdI7_wjaouC7Ngvxo',
    defaultMessage: 'flight',
    description: 'Product type display name for flight offers in commission details',
  })

  const displayProductTypeMap: Partial<Record<App.ProductType, string>> = {
    [OFFER_TYPES.OFFER_TYPE_BED_BANK]: hotelLabel,
    [OFFER_TYPES.OFFER_TYPE_HOTEL]: hotelLabel,
    [OFFER_TYPES.OFFER_TYPE_ALWAYS_ON]: hotelLabel,
    [OFFER_TYPES.OFFER_TYPE_VILLA]: villaLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_ULTRA_LUX]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_PREMIUM]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_DELUXE]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_V2]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_CRUISE_FLASH]: cruiseLabel,
    [OFFER_TYPES.OFFER_TYPE_CRUISE]: cruiseLabel,
    [OFFER_TYPES.PRODUCT_TYPE_CAR_HIRE]: carHireLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_CUSTOM]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_INSIDER_SERIES]: tourLabel,
    [OFFER_TYPES.OFFER_TYPE_EXPERIENCE]: experienceLabel,
    [OFFER_TYPES.OFFER_TYPE_FLIGHT]: flightLabel,
  }

  const displayProductTypeName = displayProductTypeMap[productType] || dealLabel
  const upToCommissionPercentage = Math.max(maxBaseCommission || 0, greatestCommission)

  const modalTitle = intl.formatMessage({
    id: '9TyZ8tHJkvUIuRNiCcer',
    defaultMessage: 'Commission details',
    description: 'Title for the commission details modal in agent hub',
  })

  const earnEstimateText = intl.formatMessage(
    {
      id: 'mhmMCYdzjuZEly9MZENq',
      defaultMessage: 'Earn up to {percentage}% on this deal',
      description: 'Commission estimate text shown when exact commission is variable',
    },
    { percentage: upToCommissionPercentage },
  )

  const earnExactText = intl.formatMessage(
    {
      id: 'A_XGRPDSNaWYD5DLLItx',
      defaultMessage: 'Earn a {percentage}% commission on this deal',
      description: 'Commission text shown when exact commission percentage is known',
    },
    { percentage: commissionPercentage },
  )

  const howItWorksLabel = intl.formatMessage({
    id: 'tMceyQaJUwPR_nEbnyyh',
    defaultMessage: 'How it works:',
    description: 'Heading for the commission explanation list in details modal',
  })

  const commissionCalculationText = useMemo(() => {
    if (includesAllFees) {
      return intl.formatMessage(
        {
          id: 'fN7xFBvtftTYvmzyjSck',
          defaultMessage: 'Your commission is calculated from the total booking amount for this {productType}.',
          description: 'Explains how agent commission is calculated from the total booking amount for a given product type',
        },
        { productType: displayProductTypeName },
      )
    }

    return intl.formatMessage(
      {
        id: 'BSCfSMqBnOgb5gXEy_Wh',
        defaultMessage: 'Your commission is calculated from the commissionable fare amount for this {productType}.',
        description: 'Explains how agent commission is calculated from the commissionable fare amount for a given product type',
      },
      { productType: displayProductTypeName },
    )
  }, [displayProductTypeName, includesAllFees, intl])

  const variableCommissionText = intl.formatMessage({
    id: 'hVgZtUdBSc7xFn_s6kpg',
    defaultMessage: 'Commission is variable for this offer and will only be calculated once dates have been selected and rates returned by our suppliers',
    description: 'Explains that bed bank offer commission varies based on selected dates and supplier rates',
  })

  const commissionRangeText = intl.formatMessage(
    {
      id: '2tQZVJF1R3POQCBky4HG',
      defaultMessage: 'Once the rates are returned, we will calculate the commission which will range from {minPercentage}% to {maxPercentage}%',
      description: 'Explains the commission percentage range for bed bank offers',
    },
    { minPercentage: minBaseCommission, maxPercentage: maxBaseCommission },
  )

  const earnEstimateTextOnCommissionableFare = intl.formatMessage(
    {
      id: 'v9xfl870dp3dlvZZ-aB8',
      defaultMessage: 'Earn up to {percentage}% on the commissionable fare for this deal.',
      description: 'Commission estimate text shown when exact commission is variable',
    },
    { percentage: upToCommissionPercentage },
  )

  const earnExactTextOnCommissionableFare = intl.formatMessage(
    {
      id: 'G4YyVX6epVWm4FzGwQ3k',
      defaultMessage: 'Earn a {percentage}% commission on the commissionable fare for this deal.',
      description: 'Commission text shown when exact commission percentage is known',
    },
    { percentage: commissionPercentage },
  )

  const commissionTextToShow = useMemo(() => {
    if (multipleCommissions && includesAllFees) return earnEstimateText
    if (multipleCommissions) return earnEstimateTextOnCommissionableFare
    if (includesAllFees) return earnExactText
    return earnExactTextOnCommissionableFare
  }, [multipleCommissions, includesAllFees, earnEstimateText, earnEstimateTextOnCommissionableFare, earnExactText, earnExactTextOnCommissionableFare])

  const netPaymentText = useMemo(() => {
    if (includesAllFees) {
      return intl.formatMessage({
        id: '9QJA-AjGiSneTC1TWJNz',
        defaultMessage: 'Make the net payment, which accounts for the deduction of the commission from your total booking cost.',
        description: 'Explains that agent pays net amount after commission deduction from the total booking cost',
      })
    }

    return intl.formatMessage({
      id: 'xdPNdaRntlPP9i26objn',
      defaultMessage: 'Make the net payment, which accounts for the deduction of the commission from your commissionable fare cost.',
      description: 'Explains that agent pays net amount after commission deduction from the commissionable fare cost',
    })
  }, [includesAllFees, intl])

  const monitorCommissionsText = intl.formatMessage({
    id: '0wzU3KfRprSU3op4iick',
    defaultMessage: 'Easily monitor your commissions in real-time through your agent dashboard.',
    description: 'Informs agents they can track commissions via their dashboard',
  })

  const showModal = useContext(ModalContext)
  const openModal = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    showModal(<Modal title={modalTitle}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large">
          {commissionTextToShow}
        </BodyText>
        <BodyText variant="large" weight="bold">
          {howItWorksLabel}
        </BodyText>
        <List>
          <BodyText variant="large" as="li">
            {commissionCalculationText}
          </BodyText>
          {productType === OFFER_TYPES.OFFER_TYPE_BED_BANK && <>
            <BodyText variant="large" as="li">
              {variableCommissionText}
            </BodyText>
            <BodyText variant="large" as="li">
              {commissionRangeText}
            </BodyText>
          </>}
          <BodyText variant="large" as="li">
            {netPaymentText}
          </BodyText>
          <BodyText variant="large" as="li">
            {monitorCommissionsText}
          </BodyText>
        </List>
      </VerticalSpacer>
    </Modal>)
  }, [showModal, modalTitle, commissionTextToShow, howItWorksLabel, commissionCalculationText, productType, variableCommissionText, commissionRangeText, netPaymentText, monitorCommissionsText])

  const commissionContent = useMemo<React.ReactNode>(() => {
    if (hasTotal && multipleCommissions && includesAllFees) {
      return (
        <FormattedMessage
          id="yQruNTFdW9EOa_7IwVho"
          defaultMessage="Earn up to {amount} commission ({percentage}%)"
          description="Commission link text showing estimated commission amount and percentage for offers with multiple commission rates"
          values={{
            amount: <FormatCurrency value={offerTotal * (greatestCommission / 100)} />,
            percentage: greatestCommission,
          }}
        />
      )
    }

    if (hasTotal && multipleCommissions) {
      return (
        <FormattedMessage
          id="yE61_ilbyGBAfhVBAxoG"
          defaultMessage="Earn up to {amount} commission ({percentage}%) on commissionable fare"
          description="Commission link text showing estimated commission amount and percentage excluding taxes and fees"
          values={{
            amount: <FormatCurrency value={offerTotal * (greatestCommission / 100)} />,
            percentage: greatestCommission,
          }}
        />
      )
    }

    if (hasTotal && includesAllFees) {
      return (
        <FormattedMessage
          id="x7_kL_wnNienRNWfHUss"
          defaultMessage="Earn {amount} commission ({percentage}%)"
          description="Commission link text showing exact commission amount and percentage"
          values={{
            amount: <FormatCurrency value={offerTotal * (greatestCommission / 100)} />,
            percentage: greatestCommission,
          }}
        />
      )
    }

    if (hasTotal) {
      return (
        <FormattedMessage
          id="LXW-0i023fWm4e4UnzkW"
          defaultMessage="Earn {amount} commission ({percentage}%) on commissionable fare"
          description="Commission link text showing exact commission amount and percentage excluding taxes and fees"
          values={{
            amount: <FormatCurrency value={offerTotal * (greatestCommission / 100)} />,
            percentage: greatestCommission,
          }}
        />
      )
    }

    if (multipleCommissions && includesAllFees && productType !== OFFER_TYPES.OFFER_TYPE_BED_BANK) {
      return intl.formatMessage(
        {
          id: 'LaVGAQw04SPDtQdSGJaI',
          defaultMessage: 'Earn up to {percentage}% commission',
          description: 'Commission link text showing estimated commission percentage for offers with multiple rates',
        },
        { percentage: greatestCommission },
      )
    }

    if (multipleCommissions && !includesAllFees) {
      return intl.formatMessage(
        {
          id: '65FU-vp1zVwXB5oIoNdR',
          defaultMessage: 'Earn up to {percentage}% commission on commissionable fare',
          description: 'Commission link text showing estimated commission percentage excluding taxes and fees',
        },
        { percentage: greatestCommission },
      )
    }

    if (includesAllFees) {
      return intl.formatMessage(
        {
          id: 'aWApPw5ERyYChUhiw0eb',
          defaultMessage: 'Earn {percentage}% commission',
          description: 'Commission link text showing exact commission percentage for agent offers',
        },
        { percentage: greatestCommission },
      )
    }

    return intl.formatMessage(
      {
        id: 'zmwU2Vz_Sf4xF3jURIce',
        defaultMessage: 'Earn {percentage}% commission on commissionable fare',
        description: 'Commission link text showing exact commission percentage excluding taxes and fees',
      },
      { percentage: greatestCommission },
    )
  }, [greatestCommission, hasTotal, includesAllFees, intl, multipleCommissions, offerTotal, productType])

  const incentiveCommissionContent = intl.formatMessage({
    id: 'IVjptlsmjW-sN0_6Ubpt',
    defaultMessage: '+{percentage}% incentive commission',
    description: 'Incentive commission text showing exact commission percentage for agent offers',
  }, { percentage: incentivePercentage })

  return (
    <Group direction="vertical" gap={4}>
      <Caption variant="medium">
        <TextLink
          colour="highlight-primary-dark"
          endIcon={<LineInfoCircleIcon colour="normal-contrast" />}
          onClick={openModal}
        >
          {commissionContent}
        </TextLink>
      </Caption>

      {incentivePercentage > 0 && (
        <UrgencyLabel icon={<SolidFireIcon />} variant="critical1">
          {incentiveCommissionContent}
        </UrgencyLabel>
      )}
    </Group>
  )
}

export default PriceRowCommission
